import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1a1a1a',
      fontFamily: '-apple-system, sans-serif',
      '::placeholder': { color: '#aaa' }
    },
    invalid: { color: '#dc2626' }
  }
};

function PaymentMethodForm() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [cardOnFile, setCardOnFile] = useState(null);
  const [showNewCard, setShowNewCard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadCard() {
      try {
        const donorDoc = await getDoc(doc(db, 'donors', auth.currentUser.uid));
        const data = donorDoc.data();

        if (data?.cardLast4) {
          setCardOnFile({
            last4: data.cardLast4,
            brand: data.cardBrand
          });
        } else {
          setShowNewCard(true);
        }
      } catch (err) {
        console.error(err);
        setShowNewCard(true);
      } finally {
        setLoading(false);
      }
    }

    loadCard();
  }, []);

  async function handleSave() {
    setError('');
    setMessage('');

    if (!stripe || !elements) {
      setError('Payment system not ready');
      return;
    }

    try {
      setSaving(true);

      const cardElement = elements.getElement(CardElement);

      const { error: stripeError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: `${userProfile.firstName} ${userProfile.lastName}`,
            email: userProfile.email
          }
        });

      if (stripeError) {
        setError(stripeError.message);
        setSaving(false);
        return;
      }

      const functions = getFunctions();
      const savePaymentMethod = httpsCallable(functions, 'savePaymentMethod');

      const result = await savePaymentMethod({
        paymentMethodId: paymentMethod.id
      });

      if (!result.data.success) {
        setError('Failed to save payment method');
        setSaving(false);
        return;
      }

      setMessage('Payment method updated successfully');
      setShowNewCard(false);

      // refresh card display
      setCardOnFile({
        last4: result.data.last4,
        brand: result.data.brand
      });

    } catch (err) {
      console.error(err);
      setError('Something went wrong');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: '2rem' }}>
      <h1 style={{ marginBottom: '1rem' }}>Payment Method</h1>

      {loading && <p>Loading...</p>}

      {!loading && (
        <>
          {cardOnFile && !showNewCard && (
            <div style={{
              background: '#f5f5f5',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>
                {cardOnFile.brand?.toUpperCase()} ending in {cardOnFile.last4}
              </span>
              <button onClick={() => setShowNewCard(true)}>
                Change
              </button>
            </div>
          )}

          {showNewCard && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1rem'
              }}>
                <CardElement options={CARD_ELEMENT_OPTIONS} />
              </div>

              {cardOnFile && (
                <button
                  onClick={() => setShowNewCard(false)}
                  style={{ marginTop: '0.5rem' }}
                >
                  Use saved card
                </button>
              )}
            </div>
          )}

          {error && (
            <div style={{ color: 'red', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          {message && (
            <div style={{ color: 'green', marginBottom: '1rem' }}>
              {message}
            </div>
          )}

          {showNewCard && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: '#1e3a5f',
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              {saving ? 'Saving...' : 'Save payment method'}
            </button>
          )}

          <div style={{ marginTop: '2rem' }}>
            <button onClick={() => navigate('/give/dashboard')}>
              ← Back to dashboard
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function PaymentMethodPage() {
  return (
    <Elements stripe={stripePromise}>
      <PaymentMethodForm />
    </Elements>
  );
}